<template>
  <div id="mapPageId" class="map-dashboard">
    <Sidebar
      ref="sidebar"
      title="Map"
      :filtersOptions="filtersOptions"
      :hasManyFilters="hasManyFilters"
      :isBigScreen="checkWidth"
      :hideFilters="hideFilters"
      :filtersCount="filtersCount"
      @apply="handleApply"
      @bounceFilter="handleBounceFilter"
      @openFilter=""
      @closeFilter="">
      <template v-slot:before-filters>
        <section style="margin-bottom: 12px; width: 100%">
          <button @click="openAllIGoogleMaps" class="google-maps-btn">
            See Route in Google Maps
          </button>
          <div class="goTo-wrapper">
            <strong class="goTo-strong">Zoom On Project:</strong>
            <el-select
              @change="lookToProjectIdAction"
              clearable
              @clear="handleClear"
              v-model="lookToProjectId"
              placeholder="Select Project"
              size="default"
              filterable>
              <el-option
                v-for="marker in markersDB"
                :key="marker"
                :label="marker.name"
                :value="marker.id" />
            </el-select>
          </div>
          <div
            v-if="props.modelGroupMapping && props.locations"
            class="goTo-wrapper"
            style="
              margin-top: 12px;
              display: flex;
              flex-direction: column;
              gap: 4px;
            ">
            <div>
              <strong class="goTo-strong">Show Reference Markers:</strong>
              <el-switch
                @change="() => updateLocations()"
                v-model="showLocations"
                :active-text="'Yes'"
                :inactive-text="'No'" />
            </div>

            <el-select
              v-if="showLocations"
              @change="() => updateLocations()"
              v-model="selectedModels"
              placeholder="Select the type of reference marker"
              size="default"
              collapse-tags
              collapse-tags-tooltip
              multiple
              filterable
              clearable>
              <el-option
                v-for="model in props.modelGroupMapping"
                :key="model"
                :label="model.name"
                :value="model.id" />
            </el-select>
            <el-select
              v-if="showLocations"
              @change="updateLocations"
              v-model="selectedLocations"
              placeholder="Select a reference markers"
              size="default"
              collapse-tags
              collapse-tags-tooltip
              multiple
              filterable
              clearable>
              <el-option
                v-for="locations in getAvailableLocations"
                :key="locations"
                :label="locations.name"
                :value="locations.id" />
            </el-select>
          </div>
        </section>
      </template>
      <template v-slot:after-filters>
        <div
          v-if="groupedRemindersData && true"
          style="margin-top: 12px; width: 100%">
          <div v-for="group in groupedRemindersData">
            <p style="font-weight: 700">{{ group.group_name }}</p>
            <div class="items-btns-wrapper" style="margin-bottom: 8px">
              <button
                v-for="item in group.reminders"
                @click="reminderGroupClicked(item)"
                :class="
                  selectedReminderGroup.findIndex((g) => g.name === item.name) >
                  -1
                    ? 'active-reminder'
                    : 'non-active-reminder'
                ">
                <div class="reminder-item">
                  {{ item.name }}
                  <section
                    v-show="
                      selectedReminderGroup.findIndex(
                        (g) => g.name === item.name
                      ) > -1
                    "
                    :style="{ background: item.color }"
                    class="reminder-item-dot"></section>
                </div>
              </button>
            </div>
          </div>
        </div>
      </template>
    </Sidebar>

    <div class="main-content-container" style="width: 82%; position: relative">
      <div id="mapPageIdMapWrapper">
        <div id="mainMap" style="width: 100%; height: 100%"></div>
      </div>

      <div v-if="!isPublic" class="mu-status-info">
        <ul>
          <li v-for="status in MONITORING_STATUS_OBJECTS" :key="status.title">
            <div
              :style="{
                background: status.color,
                marginRight: '8px',
                width: '12px',
                height: '12px',
                borderRadius: '50%'
              }"></div>
            <span>{{ status.title }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="marker-legends">
      <div
        v-for="projType in projectTypes"
        :key="projType.id"
        @click="toggleProjectType(projType.id)"
        class="item"
        :style="getBackground(projType.id)">
        <img :src="getIconUrl(projType)" alt="" />
        {{ projType.name }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import $ from 'jquery'
  import axios from 'axios'
  import { ref, onMounted, defineProps, computed, nextTick, watch } from 'vue'

  import { ElNotification } from 'element-plus'
  import {
    getProjects,
    getFilteredProjects,
    createMarkerIcon,
    MONITORING_STATUS_OBJECTS
  } from './services/service.js'

  import { FilterOption } from '../sidebar/type/type'
  import Sidebar from '../sidebar/tsSidebar.vue'

  import './stylesheets/map.scss'
  import { Location } from '../../types/index.js'

  export type Project = {
    id: number
    name: string
    project_stage_id: number
    country_id: number
    project_type_id: number
    project_phase_id: number
    latitude: number
    longitude: number
    monitored: boolean
    completion_date: string
    district_id: number
    zone: string
    donors_id: number[]
    micro_view: string
    donor_page: string
    coloredSvgString: string
    reminders: Reminder[]
  }

  type Reminder = {
    id: number
    name: string
    model_group_mapping_id: number
    color: string
    model_group_mapping: {
      name: string
    }
  }

  type GroupedReminder = {
    group_name: string
    reminders: GroupedReminderItem[]
  }

  type GroupedReminderItem = {
    ids: number[]
    name: string
    color: string
  }

  type ProjectType = {
    id: number
    name: string
    color: string
  }

  type ProjectForm = {
    content_updated_at: string
    form_url: string
    name: string
  }

  const props = defineProps<{
    isPublic: boolean
    hasManyFilters: boolean
    allReminders?: Reminder[]
    projectsMonitoringStatus?: any[]
    filtersOptions: FilterOption[]
    groupedRemindersData?: GroupedReminder[]
    locations?: (Location & { color: string })[]
    modelGroupMapping?: any
  }>()

  const windowWidth = ref(window.innerWidth)
  const projects = ref<Project[] | null>(null)
  const markersDB = ref<Project[]>([])
  const projectTypes = ref<ProjectType[]>([])
  const selectedProjectTypes = ref<number[]>([])
  const lookToProjectId = ref()
  const selectedReminderGroup = ref<GroupedReminderItem[]>([])
  const hideFilters = ref(false)
  const filtersCount = ref<{ filterID: number; projects: number[] }[]>([])

  const showLocations = ref(true)
  const selectedModels = ref<number[]>([])
  const selectedLocations = ref<number[]>([])

  let map
  const googleMapsMarkers = ref<google.maps.marker.AdvancedMarkerElement[]>([])
  const googleMapsLocations = ref<google.maps.marker.AdvancedMarkerElement[]>([])

  const handleApply = async (filters: {} | {}[]) => {
    if (Array.isArray(filters)) {
      if (filters.length > 0) {
        let filteredProjects: [Project][] = []
        for (let i = 0; i < filters.length; i++) {
          if (filters[i].filterIsShown) {
            let currentProjects = markersDB.value
            const currentFilteredProjects = getFilteredProjects(
              currentProjects,
              filters[i]
            )
            filteredProjects.push(currentFilteredProjects)
            const filterCountIndex = filtersCount.value.findIndex(
              (item) => item.filterID === filters[i].id
            )
            if (filterCountIndex > -1) {
              filtersCount.value[filterCountIndex].projects =
                currentFilteredProjects.map((p) => p.id)
            } else {
              filtersCount.value.push({
                filterID: filters[i].id,
                projects: currentFilteredProjects.map((p) => p.id)
              })
            }
          }
        }

        const uniqueProjects = filteredProjects
          .flat()
          .filter(
            (project, index, self) =>
              index === self.findIndex((t) => t.id === project.id)
          )

        projects.value =
          uniqueProjects.length > 0 ? uniqueProjects : markersDB.value
      } else {
        projects.value = markersDB.value
      }
    }

    updateMarkers()
  }

  const handleBounceFilter = (filterID: number) => {
    const filterCountIndex = filtersCount.value.findIndex(
      (item) => item.filterID === filterID
    )
    if (filterCountIndex > -1) {
      const filterProjects = filtersCount.value[filterCountIndex].projects
      googleMapsMarkers.value.forEach((marker) => {
        if (marker.map && filterProjects.includes(parseInt(marker.id))) {
          if (marker.content instanceof HTMLElement) {
            marker.content.classList.add('bounce')
          }
        } else {
          if (marker.content instanceof HTMLElement) {
            marker.content.classList.remove('bounce')
          }
        }
      })
    } else {
      googleMapsMarkers.value.forEach((marker) => {
        if (marker.content instanceof HTMLElement) {
          marker.content.classList.remove('bounce')
        }
      })
    }
  }

  const onResize = () => {
    windowWidth.value = window.innerWidth
  }

  const formatDate = (date) => {
    if (typeof date === 'string') {
      date = date.replace(' UTC', '+0000')
      date = new Date(date)
    }

    const options = { year: 'numeric', month: 'short', day: 'numeric' }
    const localDateString = date.toLocaleDateString('en-US', options)

    return localDateString
  }

  const getMarkerIcon = (marker) => {
    const markerIcon = document.createElement('img')
    // markerIcon.src = `data:image/svg+xml;base64,${window.btoa(
    //   marker.coloredSvgString
    // )}`
    markerIcon.src = `data:image/svg+xml;base64,${window.btoa(
      marker.coloredSvgString
    )}`
    markerIcon.width = 16
    markerIcon.height = 21

    if (selectedReminderGroup.value.length > 0 && marker.reminders.length > 0) {
      const currentProjectType = projectTypes.value.find(
        (projType) => projType.id === marker.project_type_id
      )
      let dataUriObj = createMarkerIcon(
        marker,
        selectedReminderGroup.value,
        currentProjectType
      )

      markerIcon.src = dataUriObj.url
      markerIcon.width = dataUriObj.scaledSize.width
      markerIcon.height = dataUriObj.scaledSize.height
    }

    return markerIcon
  }

  const getLocationIcon = (color: string) => {
    const locationIcon = document.createElement('img')
    locationIcon.src = `data:image/svg+xml;base64,${window.btoa(
      `<svg xmlns="http://www.w3.org/2000/svg" width="15.53" height="24.85" viewBox="0 0 15.53 24.85">
        <defs>
          <style>
            .cls-1 {
              fill: ${color};
            }

            .cls-2 {
              fill: ${color};
            }
          </style>
        </defs>
  <path class="cls-1" id="Icon_metro-location" data-name="Icon metro-location" d="M16.12,1.928A7.765,7.765,0,0,0,8.355,9.694c0,7.766,7.765,17.084,7.765,17.084s7.765-9.319,7.765-17.084A7.765,7.765,0,0,0,16.12,1.928Zm0,12.522a4.756,4.756,0,1,1,4.756-4.756A4.756,4.756,0,0,1,16.12,14.45Z" transform="translate(-8.355 -1.928)"/>
  <path class="cls-2" id="Ellipse_118" data-name="Ellipse 118" d="M3,0A3,3,0,1,1,0,3,3,3,0,0,1,3,0Z" transform="translate(4.765 4.725)"/>
  </svg>
  `
    )}`
    locationIcon.width = 16
    locationIcon.height = 21
    return locationIcon
  }

  const getBackground = (projectTypeID: number) => {
    const index = selectedProjectTypes.value.findIndex(
      (projType) => projType === projectTypeID
    )
    if (index !== -1) {
      return {
        background: '#c0c0c0'
      }
    }
  }

  const getIconUrl = (projectType: ProjectType) => {
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="15.531" height="24.849" viewBox="0 0 15.531 24.849">
        <defs>
          <style>
            .cls-1 {
              fill: #707070;
            }
          </style>
        </defs>
        <path id="Icon_metro-location" data-name="Icon metro-location" class="cls-1" d="M16.12,1.928A7.765,7.765,0,0,0,8.355,9.693c0,7.765,7.765,17.084,7.765,17.084s7.765-9.318,7.765-17.084A7.765,7.765,0,0,0,16.12,1.928Zm0,12.522a4.756,4.756,0,1,1,4.756-4.756A4.756,4.756,0,0,1,16.12,14.45ZM13.111,9.693A3.009,3.009,0,1,1,16.12,12.7,3.009,3.009,0,0,1,13.111,9.693Z" transform="translate(-8.355 -1.928)"/>
      </svg>
      `
    let coloredSvgString = svgString.replace('#707070', `${projectType.color}`)
    let dataUri = `data:image/svg+xml;base64,${window.btoa(coloredSvgString)}`
    return dataUri
  }

  const toggleProjectType = (projectTypeID: number) => {
    const index = selectedProjectTypes.value.findIndex(
      (projType) => projType === projectTypeID
    )
    if (index === -1) {
      selectedProjectTypes.value.push(projectTypeID)
    } else {
      selectedProjectTypes.value.splice(index, 1)
    }

    updateMarkers()
  }

  const reminderGroupClicked = (clickedGroup: GroupedReminderItem) => {
    let index = selectedReminderGroup.value.findIndex(
      (group) => group.name === clickedGroup.name
    )
    if (index > -1) {
      selectedReminderGroup.value.splice(index, 1)
    } else {
      selectedReminderGroup.value.push(clickedGroup)
    }

    markersDB.value.forEach((project) => {
      const marker = googleMapsMarkers.value.find(
        (marker) => parseInt(marker.id) === project.id
      )
      if (marker) {
        marker.content = getMarkerIcon(project)
      }
    })
  }

  const openAllIGoogleMaps = () => {
    const onMapMarkers = googleMapsMarkers.value.filter(
      (marker) => marker.map === map
    )
    if (onMapMarkers.length > 1) {
      let points = JSON.parse(JSON.stringify(onMapMarkers))
      let waypoints = points
        .slice(2)
        .map((point) => `${point.position.lat()},${point.position.lng()}`)
        .join('|')
      window.open(
        `https://www.google.com/maps/dir/?api=1&origin=${points[0].position.lat()},${points[0].position.lng()}&destination=${points[1].position.lat()},${points[1].position.lng()}&waypoints=${waypoints}`,
        '_blank'
      )
    } else {
      ElNotification({
        title: 'You must have at least two markers',
        duration: 3000
      })
    }
  }

  const handleClear = () => {
    lookToProjectId.value = null
    nextTick(() => {
      updateMarkers(false)
    })
  }

  const lookToProjectIdAction = () => {
    const selectedMarker = googleMapsMarkers.value.find(
      (marker) => parseInt(marker.id) === lookToProjectId.value
    )
    if (!selectedMarker) return
    selectedMarker.map = map
    nextTick(() => {
      const desiredLatitude =
        typeof selectedMarker.position?.lat === 'function'
          ? selectedMarker.position.lat()
          : selectedMarker.position?.lat ?? 0

      const desiredLongitude =
        typeof selectedMarker.position?.lng === 'function'
          ? selectedMarker.position.lng()
          : selectedMarker.position?.lng ?? 0

      const zoomLevel = 10
      map.setCenter({ lat: desiredLatitude, lng: desiredLongitude })
      map.setZoom(zoomLevel)
      google.maps.event.trigger(selectedMarker, 'click')
    })
  }

  const fitBounds = () => {
    const projectsShown = googleMapsMarkers.value.filter(
      (marker) => marker.map === map
    )
    if (!projectsShown || projectsShown.length === 0) {
      console.error('Projects not found')
      return
    }
    const bounds = new google.maps.LatLngBounds()
    projectsShown.forEach((marker) => {
      const position = new google.maps.LatLng(
        typeof marker.position?.lat === 'function'
          ? marker.position.lat()
          : marker.position?.lat ?? 0,
        typeof marker.position?.lng === 'function'
          ? marker.position.lng()
          : marker.position?.lng ?? 0
      )
      bounds.extend(position)
    })
    map.fitBounds(bounds)
  }

  const createMarkers = async () => {
    const markerLibraries = await google.maps.importLibrary('marker')
    const { AdvancedMarkerElement } =
      markerLibraries as google.maps.MarkerLibrary

    let markers: google.maps.marker.AdvancedMarkerElement[] = []
    if (!projects.value) return
    projects.value.forEach(async (project) => {
      const markerObj = new AdvancedMarkerElement({
        position: { lng: project.longitude, lat: project.latitude },
        title: project.name,
        content: getMarkerIcon(project)
      })

      markerObj.id = `${project.id}`

      markerObj.addListener('click', async () => {
        let projectsForms: ProjectForm[] = []

        if (!props.isPublic) {
          try {
            let projectsFormsFetch = await axios.post(
              '/project_management/forms/grid',
              {
                index: 0,
                totalCount: 3,
                additionalColumns: ['content_updated_at', 'form_url', 'name'],
                query: {
                  g: [
                    {
                      project_id_eq: project.id,
                      section_id_eq: null
                    }
                  ],
                  s: ['content_updated_at desc']
                }
              }
            )
            projectsForms = projectsFormsFetch.data.data
          } catch (error) {
            console.error('Error fetching projectsForms:', error)
          }
        }
        let contentString = `
              <div>
                <a href="${project.donor_page}" target="_blank" class="map-link">${project.name}</a>
            `

        if (!props.isPublic && projectsForms.length > 0) {
          contentString += `
                <p style="margin-bottom: 0; margin-top: 8px">
                  <a href="${project.micro_view}" target="_blank" class="map-link">Micro View</a>
                </p>
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Updated At</th>
                    </tr>
                  </thead>
                  <tbody>
              `

          projectsForms.forEach((form) => {
            contentString += `
                  <tr>
                    <td><a href="${form.form_url}" target="_blank">${
              form.name
            }</a></td>
                    <td>${formatDate(form.content_updated_at)}</td>
                  </tr>
                `
          })

          contentString += `
                  </tbody>
                </table>
              `
        } else {
          contentString += '</div>'
        }

        const infowindow = new google.maps.InfoWindow({
          content: contentString,
          ariaLabel: project.name
        })

        infowindow.open({
          anchor: markerObj,
          map
        })
      })

      if (project.id === lookToProjectId.value) {
        google.maps.event.trigger(markerObj, 'click')
      }
      markers.push(markerObj)
    })
    googleMapsMarkers.value = markers

    if (props.modelGroupMapping && props.locations && showLocations.value) {
      let locations: google.maps.marker.AdvancedMarkerElement[] = []
      props.locations.forEach((location) => {
        const markerLocation = new AdvancedMarkerElement({
          position: {
            lng: parseFloat(location.longitude),
            lat: parseFloat(location.latitude)
          },
          title: location.name,
          content: getLocationIcon(location.color)
        })
        markerLocation.id = `${location.id}`

        markerLocation.addListener('click', async () => {
          const infowindow = new google.maps.InfoWindow({
            content: `<div>${location.name}</div>`,
            ariaLabel: location.name
          })

          infowindow.open({
            anchor: markerLocation,
            map
          })
        })
        locations.push(markerLocation)
      })
      googleMapsLocations.value = locations
    }
  }

  const createMap = async () => {
    const mapLibraries = await google.maps.importLibrary('maps')
    const { Map } = mapLibraries as google.maps.MapsLibrary
    if (!map) {
      const element = document.getElementById('mainMap')
      if (!element) {
        console.error('Map container element not found')
        return
      } else {
        map = new Map(element, {
          center: { lat: 1.1219, lng: 34.1368 },
          zoom: 3,
          mapId: '225b2cf3b03a598',
          fullscreenControl: true,
          streetViewControl: false,
          maxZoom: 20
        })
      }
    }
  }

  const updateMarkers = async (applyFitBounds = true) => {
    if (!projects.value) return
    let projectsToShow = JSON.parse(JSON.stringify(projects.value))
    if (selectedProjectTypes.value.length > 0) {
      projectsToShow = projectsToShow.filter((project) => {
        if (selectedProjectTypes.value.includes(project.project_type_id))
          return project
      })
    }

    if (lookToProjectId.value) {
      const selectedMarker = markersDB.value!.find(
        (marker) => marker.id === lookToProjectId.value
      )
      if (selectedMarker) {
        if (
          !projectsToShow.find(
            (project) => project.id === lookToProjectId.value
          )
        )
          projectsToShow.push(selectedMarker)
      }
    }

    const idsToShow = projectsToShow.map((project) => project.id)

    googleMapsMarkers.value.forEach((marker) => {
      if (idsToShow.includes(parseInt(marker.id))) {
        marker.map = map
      } else {
        marker.map = null
      }
    })

    if (applyFitBounds) fitBounds()
  }

  const updateLocations = async () => {
    if (props.modelGroupMapping && props.locations) {
      if (!showLocations.value) {
        googleMapsLocations.value.forEach((location) => {
          location.map = null
        })
      } else {
        let selectedLocationsData = props.locations
        if (selectedModels.value.length > 0) {
          selectedLocationsData = selectedLocationsData.filter((location) =>
            selectedModels.value.includes(location.model_group_mapping_id)
          )
          if (selectedLocations.value.length > 0) {
            selectedLocationsData = selectedLocationsData.filter((location) =>
              selectedLocations.value.includes(location.id)
            )
          }
        } else if (selectedLocations.value.length > 0) {
          selectedLocationsData = selectedLocationsData.filter((location) =>
            selectedLocations.value.includes(location.id)
          )
        }
        const locationsToShowIds = selectedLocationsData.map(
          (location) => location.id
        )

        googleMapsLocations.value.forEach((location) => {
          if (locationsToShowIds.includes(parseInt(location.id))) {
            location.map = map
          } else {
            location.map = null
          }
        })
      }
    }
  }

  onMounted(async () => {
    window.addEventListener('resize', onResize)
    onResize()

    if (checkWidth.value) {
      $('.main-content-container').css('width', '82%')
    } else {
      $('.main-content-container').css('width', '100%')
    }

    let mainHeaderHeight = $('#mainHeader').innerHeight()
    $('#mapPageId').css('height', `calc(100vh - ${mainHeaderHeight}px)`)
    $('#mapPageIdMapWrapper').css(
      'height',
      `calc(100vh - ${mainHeaderHeight}px)`
    )

    const projectsOption = props.filtersOptions.find(
      (item) => item.field === 'project_id'
    )

    const markers = getProjects(
      projectsOption?.options,
      props.filtersOptions,
      props.projectsMonitoringStatus,
      props.allReminders
    )
    projects.value = markers
    markersDB.value = markers

    const projectTypesFilterOption = props.filtersOptions.find(
      (item) => item.field === 'project_type_id'
    )
    if (projectTypesFilterOption && projectTypesFilterOption.options)
      projectTypes.value = projectTypesFilterOption.options as ProjectType[]

    await createMap()
    await createMarkers()

    nextTick(() => {
      updateLocations()
      updateMarkers()
    })
  })

  const checkWidth = computed(() => {
    return windowWidth.value > 991
  })

  const getAvailableLocations = computed(() => {
    if (!props.locations) return []
    let availableLocations = props.locations
    if (selectedModels.value.length > 0) {
      availableLocations = availableLocations.filter((location) =>
        selectedModels.value.includes(location.model_group_mapping_id)
      )
    }
    return availableLocations
  })

  watch(
    () => windowWidth.value,
    async (newVal) => {
      if (newVal) {
        if (checkWidth.value) {
          $('.main-content-container').css('width', '82%')
        } else {
          $('.main-content-container').css('width', '100%')
        }
      }
    },
    { deep: true }
  )
</script>

<style lang="scss">
  @keyframes drop {
    0% {
      transform: translateY(-200px) scaleY(0.9);
      opacity: 0;
    }
    5% {
      opacity: 0.7;
    }
    50% {
      transform: translateY(0px) scaleY(1);
      opacity: 1;
    }
    65% {
      transform: translateY(-17px) scaleY(0.9);
      opacity: 1;
    }
    75% {
      transform: translateY(-22px) scaleY(0.9);
      opacity: 1;
    }
    100% {
      transform: translateY(0px) scaleY(1);
      opacity: 1;
    }
  }
  .drop {
    animation: drop 0.3s linear forwards 2.5s;
  }

  .bounce {
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-30px);
    }
    60% {
      transform: translateY(-15px);
    }
  }

</style>
